import React from 'react';
import Head from '../components/Head';
import Footer from '../components/Footer';
import {
    Container,
    Card,
    Text,
    Title,
    Button,
    Group,
    Stack,
    Badge,
    List,
    ThemeIcon,
    Box,
    Alert,
    createStyles,
    rem
} from '@mantine/core';
import {
    IconCheck,
    IconBrandInstagram,
    IconLock,
    IconTicket,
    IconShieldLock,
    IconCalendarEvent
} from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
    heroWrapper: {
        background: 'radial-gradient(ellipse at 50% 60%, #d6f5d6 0%, #e8f6fb 50%, #f4fbff 100%)',
        paddingTop: rem(36),
        paddingBottom: rem(40),
        textAlign: 'center',
    },
    pageTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        letterSpacing: '-0.02em',
        fontSize: rem(44),
        color: '#155724',
        lineHeight: 1.15,

        [theme.fn.smallerThan('md')]: { fontSize: rem(34) },
        [theme.fn.smallerThan('sm')]: { fontSize: rem(28) },
    },
    pageSubtitle: {
        fontSize: rem(18),
        color: theme.colors.gray[7],
        marginTop: rem(12),
        maxWidth: rem(680),
        marginLeft: 'auto',
        marginRight: 'auto',
        lineHeight: 1.5,

        [theme.fn.smallerThan('sm')]: { fontSize: rem(15) },
    },
    pricingCard: {
        maxWidth: rem(680),
        margin: '0 auto',
        backgroundColor: '#ffffff',
        borderColor: '#a3d9a5',
    }
}));

const footerLinks = [
    { 'link': '/About', 'label': 'About' },
    { 'link': '/Support', 'label': 'Support' },
    { 'link': '/Pp', 'label': 'Privacy' },
    { 'link': '/Tos', 'label': 'Terms of Use' },
];

export default function FestivalMediaPricing() {
    const { classes } = useStyles();

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#d9ebf2' }}>
            <Head />

            {/* Hero Section */}
            <div className={classes.heroWrapper}>
                <Container size="md">
                    <Stack align="center" spacing="xs">
                        <Badge
                            color="green"
                            size="lg"
                            variant="filled"
                            leftSection={<IconLock size={14} />}
                            sx={{ paddingLeft: rem(12), paddingRight: rem(12) }}
                        >
                            Exclusive Ticketing Partner Perk
                        </Badge>
                        <Title order={1} className={classes.pageTitle}>
                            Festival Photography &amp; Videography
                        </Title>
                        <Text className={classes.pageSubtitle}>
                            Full-day on-site shooting plus complete photo and video editing — exclusively available for festivals using our ticketing platform.
                        </Text>
                    </Stack>
                </Container>
            </div>

            {/* Main Content */}
            <Container size="md" sx={{ flex: 1, paddingTop: rem(32), paddingBottom: rem(64) }}>
                <Card
                    withBorder
                    shadow="lg"
                    radius="lg"
                    p="xl"
                    className={classes.pricingCard}
                >
                    {/* Pricing Header */}
                    <Group position="apart" align="flex-start" mb="md">
                        <div>
                            <Text weight={700} size="xl" color="#155724">
                                Full Festival Day Coverage
                            </Text>
                            <Text size="sm" color="dimmed">
                                Shooting + Editing (Both Photo &amp; Video)
                            </Text>
                        </div>
                        <Box sx={{ textAlign: 'right' }}>
                            <Text weight={800} size="2.4rem" color="#155724" sx={{ lineHeight: 1 }}>
                                $500
                            </Text>
                            <Text size="xs" color="dimmed" weight={500}>per festival day</Text>
                        </Box>
                    </Group>

                    {/* Primary Ticketing Requirement Alert */}
                    <Alert
                        icon={<IconTicket size={18} />}
                        title="Exclusively for Primary Ticketing Partners"
                        color="teal"
                        radius="md"
                        mb="md"
                        variant="light"
                    >
                        This media package is offered exclusively to festivals that use our app as their primary ticketing platform.
                    </Alert>

                    {/* Lifetime Price Lock Alert */}
                    <Alert
                        icon={<IconShieldLock size={18} />}
                        title="Lifetime Rate Lock Guarantee"
                        color="blue"
                        radius="md"
                        mb="xl"
                        variant="light"
                    >
                        Pricing for this service will increase in the future. However, just like our ticketing rates,{' '}
                        <strong>your price is permanently locked in at the rate of your first booking</strong> for all future festival years.
                    </Alert>

                    {/* Features / Deliverables List */}
                    <List
                        spacing="md"
                        size="sm"
                        icon={
                            <ThemeIcon color="teal" size={22} radius="xl">
                                <IconCheck size={14} />
                            </ThemeIcon>
                        }
                        mb="xl"
                    >
                        <List.Item>
                            <strong>Personally Shot &amp; Edited by the App Founder:</strong> No outsourced subcontractors — your festival media is captured and edited directly by the creator of the app.
                        </List.Item>
                        <List.Item>
                            <strong>Both Photo &amp; Video Included:</strong> High-resolution photo galleries and edited video clips/reels for social promo, comedian sets, and next year's ticket launch.
                        </List.Item>
                        <List.Item>
                            <strong>Full Festival Atmosphere:</strong> Live sets, portraits, candid green room moments, and audience reactions.
                        </List.Item>
                        <List.Item>
                            <strong>Customized to Your Needs:</strong> Coverage and shot lists are tailored specifically to your festival's schedule and vision — we'll discuss the run of show with you beforehand.
                        </List.Item>
                    </List>

                    {/* Action CTAs */}
                    <Stack spacing="sm">
                        <Button
                            component="a"
                            href="https://instagram.com/shootsfunnypeople"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outline"
                            color="dark"
                            leftIcon={<IconBrandInstagram size={18} />}
                            size="md"
                            fullWidth
                        >
                            View Work on Instagram (@shootsfunnypeople)
                        </Button>

                        <Button
                            component="a"
                            href="https://cal.com/pete-comedy-seen/discovery"
                            target="_blank"
                            rel="noopener noreferrer"
                            color="green"
                            leftIcon={<IconCalendarEvent size={18} />}
                            size="md"
                            fullWidth
                        >
                            Discuss Dates &amp; Lock In Your Rate
                        </Button>
                    </Stack>

                    {/* Subtle Availability Note */}
                    <Text size="xs" color="dimmed" align="center" mt="md" sx={{ fontStyle: 'italic', lineHeight: 1.4 }}>
                        * Note: Coverage is handled personally by the founder and is strictly subject to calendar availability. If dates are already booked or conflicted, coverage cannot be offered.
                    </Text>
                </Card>
            </Container>

            <Footer links={footerLinks} />
        </Box>
    );
}
